import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/pages/CollegePredictorPage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Users, Star, BadgeCheck, Clock, Languages, Calendar, Video, Loader2, Award,
} from "lucide-react";

const SPECS = ["All", "Engineering", "Medical", "Design", "Management", "Study Abroad", "Government jobs", "Law", "Research"];

export default function CounsellorMarketplace() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [spec, setSpec] = useState("All");
  const [minRating, setMinRating] = useState(0);

  const load = () => {
    const params = {};
    if (spec !== "All") params.specialization = spec;
    if (minRating) params.min_rating = minRating;
    api.get("/counsellors", { params }).then((r) => setList(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [spec, minRating]);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <Header icon={Users} eyebrow="Counsellor Marketplace"
        title="Book a verified career mentor" desc="Ranked by ratings, sessions & response quality." />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {SPECS.map((s) => (
          <button key={s} data-testid={`counsellor-filter-${s.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setSpec(s)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${
              spec === s ? "bg-cyan-400 text-[#041016]" : "bg-white/5 text-slate-400 hover:text-white"
            }`}>{s}</button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-400">Min rating</span>
          {[0, 4.5, 4.8].map((r) => (
            <button key={r} onClick={() => setMinRating(r)}
              className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${minRating === r ? "bg-amber-400/20 text-amber-300" : "bg-white/5 text-slate-400"}`}>
              {r === 0 ? "Any" : `${r}+`}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.map((c, i) => (
          <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            data-testid="counsellor-card" className="rounded-2xl border border-white/8 bg-[#131C2E] p-5 flex flex-col">
            <div className="flex items-center gap-3">
              <img src={c.avatar} alt={c.name} onError={(e) => { e.currentTarget.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(c.name)}&backgroundColor=06B6D4`; }}
                className="h-14 w-14 rounded-xl object-cover" />
              <div className="min-w-0">
                <div className="flex items-center gap-1">
                  <p className="font-display font-semibold text-white truncate">{c.name}</p>
                  {c.verified && <BadgeCheck className="h-4 w-4 text-cyan-400 shrink-0" />}
                </div>
                <p className="text-xs text-slate-400 truncate">{c.headline}</p>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1 text-amber-300"><Star className="h-3.5 w-3.5 fill-amber-300" /> {c.rating}</span>
              <span className="text-slate-500">·</span>
              <span className="text-slate-400">{c.sessions_completed} sessions</span>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {c.specializations.map((s) => (
                <span key={s} className="rounded-full bg-cyan-400/10 px-2 py-0.5 text-[10px] font-medium text-cyan-300">{s}</span>
              ))}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-400">
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Replies {c.response_time}</span>
              <span className="flex items-center gap-1"><Languages className="h-3 w-3" /> {c.languages.join(", ")}</span>
              <span className="flex items-center gap-1"><Award className="h-3 w-3" /> {c.experience_years}y exp</span>
              <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {c.students_guided} guided</span>
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-white/8 pt-4">
              <div>
                <p className="font-display text-lg font-bold text-white">₹{c.fee}</p>
                <p className="text-[10px] text-slate-500">per session</p>
              </div>
              {user?.role === "student" ? (
                <BookDialog counsellor={c} />
              ) : (
                <span className="text-xs text-slate-500">Student booking</span>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function BookDialog({ counsellor }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const minDate = new Date().toISOString().split("T")[0];

  const book = async () => {
    if (!date || !slot || !topic) { toast.info("Fill date, slot & topic."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", { counsellor_id: counsellor.id, date, slot, topic });
      setConfirmed(data);
      toast.success("Session booked! +50 XP");
    } catch (e) {
      toast.error("Booking failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirmed(null); }}>
      <DialogTrigger asChild>
        <button data-testid="counsellor-book-btn"
          className="rounded-full bg-cyan-400 px-4 py-2 text-xs font-bold text-[#041016] transition-transform hover:scale-105">
          Book Session
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#131C2E] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-display">Book with {counsellor.name}</DialogTitle>
        </DialogHeader>

        {confirmed ? (
          <div className="text-center py-4">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-400/15 glow-emerald">
              <BadgeCheck className="h-7 w-7 text-emerald-400" />
            </div>
            <p className="font-semibold">Session confirmed!</p>
            <p className="text-sm text-slate-400 mt-1">{confirmed.date} at {confirmed.slot}</p>
            <a href={confirmed.meet_link} target="_blank" rel="noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2 text-sm font-bold text-[#041016]">
              <Video className="h-4 w-4" /> Join video link
            </a>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm text-slate-300">Date</label>
              <input data-testid="booking-date-picker" type="date" min={minDate} value={date} onChange={(e) => setDate(e.target.value)}
                className="mt-1 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
            </div>
            <div>
              <label className="text-sm text-slate-300">Time slot</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {counsellor.availability.map((t) => (
                  <button key={t} data-testid="booking-slot-pill" onClick={() => setSlot(t)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${slot === t ? "bg-cyan-400 text-[#041016]" : "bg-white/5 text-slate-300"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-300">Topic</label>
              <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Choosing between CSE and ECE"
                className="mt-1 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
            </div>
            <button data-testid="booking-confirm-btn" onClick={book} disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-400 px-6 py-3 text-sm font-bold text-[#041016] disabled:opacity-50">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Calendar className="h-4 w-4" /> Confirm booking · ₹{counsellor.fee}</>}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
